import * as THREE from 'three';

export type CharacterAnimState =
  | 'idle'
  | 'walk'
  | 'turn'
  | 'wait'
  | 'sit'
  | 'standUp'
  | 'serveTable';

export interface CharacterClipMap {
  idle: string;
  walk: string;
  wait: string;
  sitEnter: string;
  sitIdle: string;
  sitExit: string;
  serve: string;
}

const DEFAULT_CLIPS: CharacterClipMap = {
  idle: 'Idle_Loop',
  walk: 'Walk_Loop',
  wait: 'Idle_Loop',
  sitEnter: 'Sitting_Enter',
  sitIdle: 'Sitting_Idle_Loop',
  sitExit: 'Sitting_Exit',
  serve: 'PickUp_Table',
};

export class CharacterAnimationFsm {
  readonly root: THREE.Object3D;
  readonly mixer: THREE.AnimationMixer;
  private actions = new Map<string, THREE.AnimationAction>();
  private state: CharacterAnimState = 'idle';
  private current: THREE.AnimationAction | null = null;
  private fade = 0.28;

  constructor(root: THREE.Object3D, clips: THREE.AnimationClip[], clipMap: CharacterClipMap = DEFAULT_CLIPS) {
    this.root = root;
    this.mixer = new THREE.AnimationMixer(root);
    const names = new Map(clips.map((c) => [c.name, c]));
    const bind = (key: keyof CharacterClipMap) => {
      const name = clipMap[key];
      const clip = names.get(name);
      if (clip) this.actions.set(key, this.mixer.clipAction(clip));
    };
    (Object.keys(clipMap) as (keyof CharacterClipMap)[]).forEach(bind);
    this.playState('idle', 0);
  }

  getState(): CharacterAnimState {
    return this.state;
  }

  update(dt: number): void {
    this.mixer.update(dt);
    if (this.state === 'turn') {
      /* rotación externa; volver a idle cuando termine el giro */
    }
  }

  playState(next: CharacterAnimState, fade = this.fade): void {
    if (this.state === next && this.current?.isRunning()) return;
    this.state = next;
    let action: THREE.AnimationAction | undefined;
    switch (next) {
      case 'idle':
      case 'turn':
        action = this.actions.get('idle');
        break;
      case 'walk':
        action = this.actions.get('walk');
        break;
      case 'wait':
        action = this.actions.get('wait');
        break;
      case 'sit':
        action = this.actions.get('sitEnter');
        break;
      case 'standUp':
        action = this.actions.get('sitExit');
        break;
      case 'serveTable':
        action = this.actions.get('serve');
        break;
      default:
        action = this.actions.get('idle');
    }
    if (!action) return;
    action.reset();
    action.setLoop(THREE.LoopRepeat, Infinity);
    if (next === 'sit' || next === 'standUp' || next === 'serveTable') {
      action.setLoop(THREE.LoopOnce, 1);
      action.clampWhenFinished = true;
    }
    action.enabled = true;
    action.fadeIn(fade).play();
    if (this.current && this.current !== action) this.current.fadeOut(fade);
    this.current = action;
  }

  /** Tras Sitting_Enter, pasar a idle sentado. */
  playSitIdle(): void {
    this.state = 'sit';
    const action = this.actions.get('sitIdle');
    if (!action) return;
    action.reset().fadeIn(this.fade).play();
    if (this.current) this.current.fadeOut(this.fade);
    this.current = action;
    action.setLoop(THREE.LoopRepeat, Infinity);
  }

  whenActionFinished(cb: () => void): void {
    this.mixer.addEventListener('finished', cb);
  }

  clipDuration(state: CharacterAnimState): number {
    const map: Record<CharacterAnimState, keyof CharacterClipMap | null> = {
      idle: 'idle',
      walk: 'walk',
      wait: 'wait',
      turn: 'idle',
      sit: 'sitEnter',
      standUp: 'sitExit',
      serveTable: 'serve',
    };
    const key = map[state];
    if (!key) return 0.5;
    return this.actions.get(key)?.getClip().duration ?? 0.5;
  }
}
